import { instance } from "api/apiBase";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Lead } from "views/leads-id/types";

export interface CrmApplicant {
  id: number;
  role: string | null;
  first_name: string;
  last_name: string;
  middle_name?: string;
  email: string;
  application_id: number;
  application_support_agent_active?: boolean;
  application?: {
    id: number;
    status: string;
    property?: {
      id: number;
      name: string;
    };
    created_at?: string;
    updated_at?: string;
  };
}

export interface CrmContact {
  id: number;
  first_name: string;
  last_name: string;
  emails: string[];
  phones: string[];
  created_at: string;
  updated_at: string;
  leads?: Lead[];
  applicants?: CrmApplicant[];
  leads_count?: number;
  applicants_count?: number;
}

interface ContactRelatedResponse {
  contact: CrmContact;
  leads: Lead[];
  applications: unknown[];
}

// One end of a shortened link variable. `short_url` is the literal value
// inserted into outgoing SMS/email bodies; `display_url` is the full URL
// previewed in the unit submenu. Either side can be null when the link is
// unavailable for that unit (no magic link, request-showing disabled, etc).
export interface ContactUnitLink {
  short_url: string;
  display_url: string;
}

export interface ContactUnitLinks {
  unit_id: number;
  // The contact's newest lead interested in this unit. The composer sends it
  // with the message so variable resolution + the activity log bind to the
  // lead the selected unit belongs to (not an arbitrary lead of the contact).
  lead_id: number | null;
  label: string;
  // Lets the composer sync the lead.property_name chip to this specific
  // unit's property the moment it's picked for a link — see
  // syncUnitScopedNameChips in link-variable-shared.ts.
  property_name: string | null;
  // Unlike property_name, this is genuinely per-UNIT: two units on the same
  // property can resolve to different addresses when one has its own address
  // on file and the other falls back to the property's.
  property_address: string | null;
  application_link: ContactUnitLink | null;
  request_showing_link: ContactUnitLink | null;
}

// Maps the units the composer resolved links against to a single lead. Returns
// the lead when every selected unit resolves to the SAME lead; otherwise
// undefined so the server falls back to recipient-address matching. `units`
// may contain undefined entries (unselected link slots) which are ignored.
export const singleLeadForUnits = (
  units: Array<ContactUnitLinks | undefined | null>
): number | undefined => {
  const leadIds = new Set<number>();
  units.forEach((unit) => {
    if (unit?.lead_id != null) leadIds.add(unit.lead_id);
  });
  return leadIds.size === 1 ? [...leadIds][0] : undefined;
};

interface ContactUnitLinksResponse {
  units: ContactUnitLinks[];
}

// Resolved values for the contact's NON-link (name) variables, keyed by
// variable key (e.g. `lead.full_name`). LINK variables (requires_unit) are
// excluded here — they are handled by the unit_links endpoint. Empty string
// when a value is unresolved.
interface ContactVariableValuesResponse {
  variable_values: Record<string, string>;
}

interface ContactSearchResponse {
  object: "list";
  items: CrmContact[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
  extra: null;
}

const crmApi = {
  searchContacts: async (params: {
    search: string;
    page?: number;
    per_page?: number;
    only_with_applications?: boolean;
  }): Promise<ContactSearchResponse> => {
    const response = await instance.get<ContactSearchResponse>(
      `/portal/v1/contacts`,
      { params }
    );
    return response.data;
  },

  getContact: async (contactId: string): Promise<CrmContact> => {
    const response = await instance.get<CrmContact>(
      `/portal/v1/contacts/${contactId}`
    );
    return response.data;
  },

  getContactRelated: async (
    contactId: string
  ): Promise<ContactRelatedResponse> => {
    const response = await instance.get<ContactRelatedResponse>(
      `/portal/v1/contacts/${contactId}/related`
    );
    return response.data;
  },

  getLeadDetails: async (leadId: string): Promise<Lead> => {
    const response = await instance.get<Lead>(`/portal/v1/leads/${leadId}`);
    return response.data;
  },

  getContactUnitLinks: async (
    contactId: string
  ): Promise<ContactUnitLinks[]> => {
    const response = await instance.get<ContactUnitLinksResponse>(
      `/portal/v1/boom_crm/contacts/${contactId}/unit_links`
    );
    return response.data.units;
  },

  getContactVariableValues: async (
    contactId: string,
    subjectType: string,
    options?: { leadId?: number; address?: string }
  ): Promise<Record<string, string>> => {
    const response = await instance.get<ContactVariableValuesResponse>(
      `/portal/v1/boom_crm/contacts/${contactId}/variable_values`,
      {
        params: {
          subject_type: subjectType,
          lead_id: options?.leadId,
          address: options?.address
        }
      }
    );
    return response.data.variable_values;
  }
};

export const crmKeys = {
  all: ["CRM"],
  contacts: () => [...crmKeys.all, "CONTACTS"],
  contactSearch: (query: string, page?: number) => [
    ...crmKeys.contacts(),
    "SEARCH",
    query,
    page
  ],
  contact: (contactId: string) => [...crmKeys.contacts(), contactId],
  contactRelated: (contactId: string) => [
    ...crmKeys.contacts(),
    contactId,
    "RELATED"
  ],
  contactUnitLinks: (contactId: string) => [
    ...crmKeys.contacts(),
    contactId,
    "UNIT_LINKS"
  ],
  contactVariableValues: (
    contactId: string,
    subjectType: string,
    leadId?: number,
    address?: string
  ) => [
    ...crmKeys.contacts(),
    contactId,
    "VARIABLE_VALUES",
    subjectType,
    leadId ?? null,
    address ?? null
  ],
  leads: () => [...crmKeys.all, "LEADS"],
  leadDetails: (leadId: string) => [...crmKeys.leads(), leadId]
};

export const useCrmContact = (contactId: string) =>
  useQuery({
    queryKey: crmKeys.contact(contactId),
    queryFn: () => crmApi.getContact(contactId),
    enabled: !!contactId
  });

export const useCrmLeadDetails = (leadId: string) =>
  useQuery({
    queryKey: crmKeys.leadDetails(leadId),
    queryFn: () => crmApi.getLeadDetails(leadId),
    enabled: !!leadId
  });

// Distinct units across all of a contact's leads, each carrying the
// pre-shortened application/request-showing links. Used by the inbox to
// resolve link variables to a literal short URL before send.
export const useContactUnitLinks = (contactId?: string) =>
  useQuery({
    queryKey: crmKeys.contactUnitLinks(contactId ?? ""),
    queryFn: () => crmApi.getContactUnitLinks(contactId!),
    enabled: !!contactId,
    staleTime: 1000 * 60 * 5
  });

// Resolved values for the contact's name variables, used to (a) show a live
// preview next to each name variable in the inbox insert menu and (b) store
// the resolved value as the inserted chip's title so the outgoing
// SMS/email body carries literal text. LINK variables are excluded here.
export const useContactVariableValues = (
  contactId?: string,
  subjectType = "lead",
  options?: { leadId?: number; address?: string }
) =>
  useQuery({
    queryKey: crmKeys.contactVariableValues(
      contactId ?? "",
      subjectType,
      options?.leadId,
      options?.address
    ),
    queryFn: () =>
      crmApi.getContactVariableValues(contactId!, subjectType, options),
    enabled: !!contactId,
    staleTime: 1000 * 60 * 5
  });

export const useInfiniteContactSearch = (
  query: string,
  options?: {
    enabled?: boolean;
    per_page?: number;
    only_with_applications?: boolean;
  }
) =>
  useInfiniteQuery({
    queryKey: [
      ...crmKeys.contacts(),
      "INFINITE_SEARCH",
      query,
      options?.only_with_applications ?? false
    ],
    queryFn: ({ pageParam = 1 }) =>
      crmApi.searchContacts({
        search: query,
        page: pageParam,
        per_page: options?.per_page,
        only_with_applications: options?.only_with_applications
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled: options?.enabled ?? true
  });
